import { useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import ArticleList from './components/ArticleList';
import SignIn from './components/SignIn';
import UserProfile from './components/UserProfile';
import { auth, db } from './firebase';
import './App.css';

const API_URL = 'https://newsnine-96d30eb04b38.herokuapp.com';
const SURVEY_URL = 'https://y0fqj8bye12.typeform.com/to/k4pZFuT9';

function App() {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [isGridLayout, setIsGridLayout] = useState(false);
  const [error, setError] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [user, setUser] = useState(null);
  const [userPreferences, setUserPreferences] = useState([]);

  const fetchArticles = useCallback(async () => {
    setError(null);
    try {
      const today = new Date().toISOString().split('T')[0];
      let url = `${API_URL}/api/articles?date=${today}`;
      console.log('Fetching articles from:', url);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setArticles(data.articles);
    } catch (error) {
      console.error("Error fetching articles:", error);
      setError(`Failed to fetch articles. ${error.message}`);
    }
  }, []);

  const createUserInDatabase = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/user/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await response.json();
      console.log('User creation response:', data);
      return data;
    } catch (error) {
      console.error('Error creating user in database:', error);
      throw error;
    }
  };

  useEffect(() => {
    console.log('App component mounted');
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log('Auth state changed in App:', currentUser);
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            setUserPreferences(userDoc.data().preferences || []);
          } else {
            console.log('No user document found. Creating new user profile...');
            await createUserInDatabase(currentUser.uid);
            setUserPreferences([]);
          }
        } catch (error) {
          console.error("Error fetching/creating user data:", error);
          setError("Failed to fetch/create user data. Please try again later.");
        }
      } else {
        setUser(null);
        setUserPreferences([]);
      }
      setIsMenuOpen(false);
      fetchArticles();
    });

    return () => unsubscribe();
  }, [fetchArticles]);

  useEffect(() => {
    if (selectedCategory) {
      setFilteredArticles(articles.filter(article => article.category === selectedCategory));
    } else {
      setFilteredArticles(articles);
    }
  }, [selectedCategory, articles]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setIsMenuOpen(false);
  };

  const openSurvey = () => {
    window.open(SURVEY_URL, '_blank');
    setIsMenuOpen(false);
  };

  const handleUserChange = useCallback((newUser) => {
    console.log('handleUserChange called with:', newUser);
    setUser(newUser);
    if (newUser) {
      getDoc(doc(db, 'users', newUser.uid))
        .then((userDoc) => {
          if (userDoc.exists()) {
            setUserPreferences(userDoc.data().preferences || []);
          } else {
            console.log('No user document found. Creating new user profile...');
            return createUserInDatabase(newUser.uid);
          }
        })
        .then(() => {
          // Fetch user preferences again after creating the user
          return getDoc(doc(db, 'users', newUser.uid));
        })
        .then((userDoc) => {
          if (userDoc.exists()) {
            setUserPreferences(userDoc.data().preferences || []);
          } else {
            setUserPreferences([]);
          }
        })
        .catch((error) => {
          console.error("Error fetching/creating user data:", error);
          setError("Failed to fetch/create user data. Please try again later.");
        });
    } else {
      setUserPreferences([]);
    }
  }, []);

  const handleRetry = () => {
    fetchArticles();
  };

  if (error) {
    return (
      <div className="error-container">
        <p>Error: {error}</p>
        <button onClick={handleRetry}>Retry</button>
      </div>
    );
  }

  return (
    <div className="container">
      <header className="app-header">
        <div className="header-left">
          <h1 className="app-title">News Nine</h1>
          <span className="beta-disclaimer">BETA</span>
        </div>
        <div className="header-right">
          <SignIn user={user} onUserChange={handleUserChange} />
          {user && (
            <div className={`hamburger-menu ${isMenuOpen ? 'open' : ''}`} onClick={toggleMenu}>
              <div className="hamburger-icon"></div>
              <div className="hamburger-icon"></div>
              <div className="hamburger-icon"></div>
            </div>
          )}
        </div>
      </header>
      {user && (
        <nav className={`category-menu ${isMenuOpen ? 'open' : ''}`}>
          <ul>
            <li onClick={() => handleCategoryClick(null)}>All Categories</li>
            <li onClick={() => handleCategoryClick('Politics')}>Politics</li>
            <li onClick={() => handleCategoryClick('Global News')}>Global News</li>
            <li onClick={() => handleCategoryClick('Business & Finance')}>Business & Finance</li>
            <li onClick={() => handleCategoryClick('Sports')}>Sports</li>
            <li onClick={() => handleCategoryClick('Entertainment')}>Entertainment</li>
            <li className="menu-divider"></li>
            <li onClick={openSurvey}>Give Feedback</li>
            <li className="menu-divider"></li>
            <li>
              <div className="layout-toggle">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={isGridLayout}
                    onChange={() => setIsGridLayout(!isGridLayout)}
                  />
                  <span className="slider round"></span>
                </label>
                <span>{isGridLayout ? 'Grid Layout' : 'Compact'}</span>
              </div>
            </li>
          </ul>
        </nav>
      )}
      <ArticleList 
        articles={filteredArticles} 
        isGridLayout={isGridLayout} 
        selectedCategory={selectedCategory}
        user={user}
      />
      {user && <UserProfile user={user} preferences={userPreferences} />}
    </div>
  );
}

export default App;